import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { CollectionStandingsDisplay } from './CollectionStandingsDisplay';
import type { CollectionStandings } from '@moment-golf/types';

interface SeriesOption {
    _id: string;
    groupName: string;
    collectionStandings?: CollectionStandings;
    createdAt?: string;
}

function sortSeriesNewestFirst(list: SeriesOption[]): SeriesOption[] {
    return [...list].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
    });
}

const seriesIDParam = new URLSearchParams(window.location.search).get('seriesID');

/**
 * Standalone embed page rendered at /standings.
 * No authentication required.
 *
 * Two modes:
 *  - Direct link: ?seriesID=<id> — fetches that specific series by ID with no
 *    privacy filter (the host controls access). No dropdown is shown.
 *  - Public list: no seriesID param — fetches all public series and shows a
 *    dropdown so the viewer can pick one.
 *
 * Designed to be embedded in an <iframe> on external pages.
 */
export const StandingsEmbedPage: React.FC = () => {
    const isDirectLink = Boolean(seriesIDParam);

    // Direct-link mode state
    const [directSeries, setDirectSeries] = useState<SeriesOption | null>(null);
    const [directLoading, setDirectLoading] = useState(isDirectLink);
    const [directError, setDirectError] = useState<string | null>(null);

    // Public-list mode state
    const [series, setSeries] = useState<SeriesOption[]>([]);
    const [listLoading, setListLoading] = useState(!isDirectLink);
    const [selectedSeries, setSelectedSeries] = useState<SeriesOption | null>(null);
    const [listError, setListError] = useState<string | null>(null);

    const fetchDirectSeries = useCallback(async (id: string) => {
        setDirectLoading(true);
        setDirectError(null);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/mc/GetSeriesById`,
                { params: { groupID: id } },
            );
            setDirectSeries(res.data);
        } catch {
            setDirectError('Unable to load series. Please try again later.');
        } finally {
            setDirectLoading(false);
        }
    }, []);

    const fetchPublicSeries = useCallback(async () => {
        setListLoading(true);
        setListError(null);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/mc/GetPublicSeries`,
            );
            setSeries(sortSeriesNewestFirst(res.data as SeriesOption[]));
        } catch {
            setListError('Unable to load series. Please try again later.');
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isDirectLink) {
            fetchDirectSeries(seriesIDParam!);
        } else {
            fetchPublicSeries();
        }
    }, [isDirectLink, fetchDirectSeries, fetchPublicSeries]);

    const loading = isDirectLink ? directLoading : listLoading;
    const error = isDirectLink ? directError : listError;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                overflow: 'hidden',
                p: 1,
                boxSizing: 'border-box',
            }}
        >
            {/* Fills the iframe via % of parent — avoid vw/dvw which size to the
                viewport and can overflow the host iframe cell */}
            <Paper
                elevation={0}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: 600,
                    mx: 'auto',
                    backgroundColor: 'white',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1.5,
                    overflow: 'hidden',
                    mb: 0.5,
                    boxSizing: 'border-box',
                }}
            >
                {isDirectLink ? (
                    /* ── Direct-link mode: title only, no dropdown ── */
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ flexShrink: 0, mb: 1.5 }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, color: 'primary.main' }}
                            >
                                {directSeries ? directSeries.groupName : 'Series Standings'}
                            </Typography>
                        </Stack>

                        {loading ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : error ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">{error}</Typography>
                            </Box>
                        ) : (
                            <CollectionStandingsDisplay standings={directSeries?.collectionStandings} />
                        )}
                    </>
                ) : (
                    /* ── Public-list mode: title + series dropdown ── */
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ flexShrink: 0, mb: 1.5 }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, color: 'primary.main', flexShrink: 0 }}
                            >
                                Series Standings
                            </Typography>

                            <Autocomplete
                                options={series}
                                value={selectedSeries}
                                onChange={(_, val) => setSelectedSeries(val)}
                                getOptionLabel={(opt) => opt.groupName}
                                isOptionEqualToValue={(a, b) => a._id === b._id}
                                loading={listLoading}
                                noOptionsText={
                                    listLoading ? 'Loading…' : listError ? listError : 'No public series available'
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select a series…"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {listLoading && <CircularProgress size={16} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                sx={{ flex: 1, minWidth: 0 }}
                                ListboxProps={{ style: { maxHeight: 280 } }}
                            />
                        </Stack>

                        {!selectedSeries ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {listLoading
                                        ? 'Loading…'
                                        : listError
                                        ? listError
                                        : series.length === 0
                                        ? 'No public series are currently available.'
                                        : 'Select a series above to view standings.'}
                                </Typography>
                            </Box>
                        ) : (
                            <CollectionStandingsDisplay standings={selectedSeries.collectionStandings} />
                        )}
                    </>
                )}
            </Paper>

            {/* Powered-by footer */}
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 0.5, flexShrink: 0, fontSize: 10 }}
            >
                Powered by Moment Golf
            </Typography>
        </Box>
    );
};
